import moment from 'moment';
import { GroupedUniqueItem, DistributionListItem } from './list-data.utils';
import {
  BillingOrderDetail,
  Milestone,
  SalesOrder,
} from '@/services/billing/types/billing-order-detail.type';
import { IOrderDocument } from '@/types/order-document';
import { ChoicesOrderItemTypeForBilling } from '../../orders/types';

/**
 * Simplified milestone interface for the information page.
 * Contains only the fields used by BillingMilestonesByItemTable and MilestoneDetailRow.
 */
export interface SimplifiedMilestone {
  _id: string;
  paymentDate: Date;
  milestonePeriod: number;
  total: number;
  status: string;
  orderItem: {
    orderItemType: string;
    quantity: number;
  };
}

/**
 * GroupedUniqueItem variant for the information page with simplified milestones.
 */
export interface GroupedUniqueItemInfo extends Omit<GroupedUniqueItem, 'milestones'> {
  milestones: SimplifiedMilestone[];
}

/**
 * DistribuitionListItem variant for the information page.
 */
export interface DistribuitionListItemInfo extends Omit<DistributionListItem, 'items'> {
  items: GroupedUniqueItemInfo[];
}

/**
 * Transforms milestones from BillingOrderDetail to GroupedUniqueItemInfo[] format
 * for use in BillingItemsTable component.
 */
export const transformMilestonesToGroupedItems = (
  milestones: Milestone[],
  salesOrders: SalesOrder[]
): GroupedUniqueItemInfo[] => {
  // Create a map of subscription+orderItemType to aggregated amounts from salesOrders
  const aggregatedAmounts = new Map<string, number>();

  salesOrders.forEach((salesOrder) => {
    salesOrder.salesOrderItems.forEach((item) => {
      // Find the milestone to get orderItemType
      const milestone = milestones.find((m) => item.milestones.includes(m._id));
      if (!milestone) return;

      const orderType = milestone.orderItem?.orderItemType || '';
      const key = `${item.subscription}-${orderType}`;
      const currentAmount = aggregatedAmounts.get(key) || 0;
      aggregatedAmounts.set(key, currentAmount + (item.subAmount ?? item.amount));
    });
  });

  const uniqueItems = new Map<string, GroupedUniqueItemInfo>();

  milestones.forEach((milestone) => {
    const orderType =
      milestone.orderItem?.orderItemType || ChoicesOrderItemTypeForBilling.billingCloud;
    const subscription = milestone.subscription._id;
    const key = `${subscription}-${orderType}`;

    if (!uniqueItems.has(key)) {
      // Get all milestones for this subscription/orderType combination
      const relatedMilestones = milestones
        .filter(
          (m) =>
            m.subscription._id === subscription &&
            (m.orderItem?.orderItemType || ChoicesOrderItemTypeForBilling.billingCloud) ===
              orderType
        )
        .sort((a, b) => a.milestonePeriod - b.milestonePeriod);

      // Get the aggregated amount from salesOrders, fallback to milestone sum
      const aggregatedAmount = aggregatedAmounts.get(key);
      const totalAmount =
        aggregatedAmount ?? relatedMilestones.reduce((acc, m) => acc + m.total, 0);

      uniqueItems.set(key, {
        subscription,
        type: orderType,
        productName: milestone.subscription.product.name,
        plan: {
          name: milestone.subscription.plan.name,
          code: milestone.subscription.plan.code,
        },
        partnerLogo: milestone.subscription.partner.logo?.url || '',
        sku: milestone.subscription.sku.name,
        provisionDate: moment(milestone.subscription.provisionDate).utc().format('L'),
        renewalDate: moment(
          milestone.subscription.renewalDateCommercial || milestone.subscription.renewalDate
        )
          .utc()
          .format('L'),
        quantity: milestone.orderItem?.quantity || milestone.subscription.purchaseQuantity,
        totalAmount,
        milestones: relatedMilestones.map((m) => ({
          _id: m._id,
          paymentDate: m.paymentDate,
          milestonePeriod: m.milestonePeriod,
          total: m.total,
          status: m.status,
          orderItem: {
            orderItemType: m.orderItem?.orderItemType || '',
            quantity: m.orderItem?.quantity || 0,
          },
        })),
      });
    }
  });

  return Array.from(uniqueItems.values());
};

/**
 * Transforms salesOrders from BillingOrderDetail to DistribuitionListItemInfo[] format
 * for use in BillingDistributionTabs component.
 */
export const transformSalesOrdersToDistributionList = (
  salesOrders: SalesOrder[],
  milestones: Milestone[]
): DistribuitionListItemInfo[] => {
  return salesOrders.map((salesOrder) => {
    const items: GroupedUniqueItemInfo[] = [];

    salesOrder.salesOrderItems.forEach((salesOrderItem) => {
      // Find the first milestone to get product/plan details
      const firstMilestoneId = salesOrderItem.milestones[0];
      const milestone = milestones.find((m) => m._id === firstMilestoneId);

      if (milestone) {
        const orderType =
          milestone.orderItem?.orderItemType || ChoicesOrderItemTypeForBilling.billingCloud;
        const relatedMilestones = milestones
          .filter((m) => salesOrderItem.milestones.includes(m._id))
          .sort((a, b) => a.milestonePeriod - b.milestonePeriod);

        items.push({
          subscription: salesOrderItem.subscription,
          type: orderType,
          productName: milestone.subscription.product.name,
          plan: {
            name: milestone.subscription.plan.name,
            code: milestone.subscription.plan.code,
          },
          partnerLogo: milestone.subscription.partner.logo?.url || '',
          sku: milestone.subscription.sku.name,
          provisionDate: moment(milestone.subscription.provisionDate).utc().format('L'),
          renewalDate: moment(
            milestone.subscription.renewalDateCommercial || milestone.subscription.renewalDate
          )
            .utc()
            .format('L'),
          quantity: milestone.orderItem?.quantity || milestone.subscription.purchaseQuantity,
          totalAmount: salesOrderItem.subAmount ?? salesOrderItem.amount,
          milestones: relatedMilestones.map((m) => ({
            _id: m._id,
            paymentDate: m.paymentDate,
            milestonePeriod: m.milestonePeriod,
            total: m.total,
            status: m.status,
            orderItem: {
              orderItemType: m.orderItem?.orderItemType || '',
              quantity: m.orderItem?.quantity || 0,
            },
          })),
        });
      }
    });

    const documents: IOrderDocument[] = salesOrder.documents?.map((doc) => ({
      _id: doc._id,
      documentType: doc.documentType,
      file: doc.file,
      number: doc.number || '',
    })) as IOrderDocument[];

    return {
      legalEntity: {
        _id: salesOrder.legalEntity._id,
        name: salesOrder.legalEntity.legal_name,
        taxNumber: salesOrder.legalEntity.tax_number,
      },
      status: salesOrder.status,
      items,
      documents,
    };
  });
};

/**
 * Transforms BillingOrderOrganization to the format expected by OrganizationInfoCard
 */
export const transformOrganizationForCard = (
  organization: BillingOrderDetail['organization'],
  orderData: Pick<BillingOrderDetail, 'status' | 'number'>
) => {
  return {
    _id: organization._id,
    name: organization.name,
    country: organization.country,
    owner: organization.owner,
    segment: organization.segment,
    currentOrder: {
      status: orderData.status,
      number: orderData.number,
    },
  };
};
